<template>
  <button
    class="space-node flex flex-col items-center transition-transform duration-150"
    :class="[
      lesson.status === 'locked' ? 'cursor-not-allowed opacity-60' : 'cursor-pointer hover:scale-110',
      lesson.status === 'current' ? 'scale-105' : ''
    ]"
    :title="lesson.status === 'locked' ? lesson.lockMessage : ''"
    :data-lesson-status="lesson.status"
    @click="$emit('select', lesson)"
  >
    <!-- Planet node -->
    <div class="relative flex items-center justify-center" :style="{ width: '60px', height: '60px' }">
      <!-- Glow for completed -->
      <div
        v-if="lesson.status === 'completed'"
        class="absolute rounded-full"
        :style="{
          inset: '-6px',
          boxShadow: `0 0 20px 8px ${planetColor}50`,
          borderRadius: '50%'
        }"
      />
      <!-- Pulsing ring for current -->
      <div
        v-if="lesson.status === 'current'"
        class="absolute inset-[-6px] rounded-full animate-ping opacity-25"
        :style="{ backgroundColor: planetColor }"
      />
      <div
        v-if="lesson.status === 'current'"
        class="absolute inset-[-5px] rounded-full animate-pulse opacity-40 border-2"
        :style="{ borderColor: planetColor }"
      />

      <!-- Planet SVG -->
      <svg width="60" height="60" viewBox="0 0 60 60" fill="none">
        <!-- Planet body -->
        <circle cx="30" cy="30" :r="planetRadius" :fill="planetColor" />
        <!-- Surface details — bands for gas giants -->
        <template v-if="index % 3 === 1 && lesson.status !== 'locked'">
          <ellipse cx="30" cy="26" :rx="planetRadius - 2" ry="3" :fill="planetHighlight" opacity="0.2" />
          <ellipse cx="30" cy="34" :rx="planetRadius - 3" ry="2" :fill="planetHighlight" opacity="0.15" />
          <!-- Ring -->
          <ellipse cx="30" cy="32" rx="28" ry="7" stroke="white" stroke-width="1.5" fill="none" opacity="0.2" />
        </template>
        <!-- Craters for rocky planets -->
        <template v-else-if="index % 3 === 2 && lesson.status !== 'locked'">
          <circle cx="24" cy="26" r="3" :fill="planetHighlight" opacity="0.2" />
          <circle cx="34" cy="33" r="2" :fill="planetHighlight" opacity="0.15" />
        </template>
        <!-- Highlight / shine -->
        <ellipse
          v-if="lesson.status !== 'locked'"
          cx="23" cy="23" rx="8" ry="6"
          fill="white" opacity="0.2"
        />

        <!-- Status icons -->
        <template v-if="lesson.status === 'completed'">
          <!-- Trophy cup -->
          <path d="M24,22 h12 l-2,10 h-8 z" fill="white" />
          <rect x="27" y="32" width="6" height="3" rx="0.5" fill="white" />
          <rect x="25" y="35" width="10" height="2" rx="1" fill="white" />
          <path d="M24,22 Q20,22 20,26 Q20,29 24,29" stroke="white" stroke-width="1.5" fill="none" />
          <path d="M36,22 Q40,22 40,26 Q40,29 36,29" stroke="white" stroke-width="1.5" fill="none" />
        </template>
        <template v-else-if="lesson.status === 'current'">
          <polygon points="25,20 25,40 40,30" fill="white" opacity="0.9" />
        </template>
        <template v-else-if="lesson.status === 'locked'">
          <rect x="24" y="29" width="12" height="9" rx="2" fill="white" />
          <path d="M26,29 V26 a4,4 0 0 1 8,0 V29" stroke="white" stroke-width="2" fill="none" />
        </template>
        <template v-else-if="lesson.status === 'skipped'">
          <text x="30" y="35" text-anchor="middle" font-size="20" font-weight="bold" fill="white">!</text>
        </template>
        <text v-else x="30" y="34" text-anchor="middle" font-size="15" font-weight="bold" fill="white">
          {{ index + 1 }}
        </text>
      </svg>
    </div>

    <!-- Orbiting stars for completed planets -->
    <div v-if="lesson.status === 'completed' && lesson.stars > 0" class="flex gap-0.5 mt-1">
      <svg
        v-for="i in 3"
        :key="i"
        width="13" height="13" viewBox="0 0 13 13"
      >
        <polygon
          points="6.5,1 8,4.5 11.5,5 9,7.3 9.7,11 6.5,9 3.3,11 4,7.3 1.5,5 5,4.5"
          :fill="i <= lesson.stars ? '#fbbf24' : '#334155'"
          :opacity="i <= lesson.stars ? 1 : 0.3"
        />
      </svg>
    </div>

    <!-- Label -->
    <div
      class="text-[11px] text-center mt-1 leading-tight line-clamp-2 max-w-[100px] font-medium"
      :class="lesson.status === 'locked' ? 'text-red-400 font-bold' : lesson.status === 'skipped' ? 'text-orange-300' : 'text-slate-300'"
    >
      {{ lesson.title }}
    </div>
  </button>
</template>

<script setup>
import { computed } from 'vue';

const props = defineProps({
  lesson: { type: Object, required: true },
  index: { type: Number, required: true }
});

defineEmits(['select']);

// Vibrant planet colors — each planet gets a unique color by index
const planetColors = [
  '#3b82f6', // blue
  '#8b5cf6', // violet
  '#ec4899', // pink
  '#f97316', // orange
  '#06b6d4', // cyan
  '#10b981', // emerald
  '#eab308', // yellow
  '#ef4444', // red
  '#6366f1', // indigo
  '#14b8a6', // teal
  '#f472b6', // rose
  '#a855f7'  // purple
];

const planetColor = computed(() => {
  if (props.lesson.status === 'locked') return '#ef4444';
  if (props.lesson.status === 'current') return '#22d3ee';
  if (props.lesson.status === 'skipped') return '#fb923c'; // orange — needs attention
  return planetColors[props.index % planetColors.length];
});

const planetHighlight = computed(() => {
  return props.lesson.status === 'locked' ? '#475569' : 'white';
});

const planetRadius = computed(() => {
  if (props.lesson.status === 'current') return 25;
  return 21;
});
</script>
